import { mulberry32, seedFrom } from "./random.js";
import { COLORS, SHAPES, packToken, tokenColor, tokenShape } from "./sequence.js";
/**
 * The rule changed — sort by one property, then by another.
 *
 * A card appears and goes into one of two bins. For a while the rule is "sort by shape"; then it
 * becomes "sort by colour", and the bins visibly change to say so.
 *
 * **The switch used to be unannounced, and that was a mistake.** This was built as a faithful card
 * sort: both bins showed a shape AND a colour, nothing indicated which property was in force, and
 * the child was meant to discover the change from the feedback alone. That is the clinical task,
 * and the clinical task comes with a clinician sitting beside the child saying "sort these". Played
 * unaided it is not hard, it is unreadable — an adult tester could not work out what the game
 * wanted, and two bins carrying two properties each give a child no way to tell which comparison
 * they are being asked to make.
 *
 * So the bins now ARE the rule: under the shape rule each bin shows a plain grey shape, under the
 * colour rule a plain colour with no shape. When the rule changes the bins change, and a child can
 * see what is being asked. That makes it a cued task-switching game rather than an unaided
 * discovery one — an easier paradigm, and a real one.
 *
 * The cost is honest and small: it no longer observes whether a child NOTICES a change unaided.
 * It was never going to support that claim anyway — cognitive flexibility is descriptive-only in
 * the Phase 0 triage and inhibition has poor test-retest at this age `[FACT: S14]` — so what was
 * given up was a measurement the product had already agreed not to make. What remains is the
 * switch cost with a visible cue, which is still worth recording and still the only game here where
 * the ground moves.
 *
 * **What this measures, and what it deliberately does not.**
 *
 * The textbook version of this task is timed: a rule flips and the score is how much slower and
 * less accurate you get on the next few items. That version is unbuildable here. PC-1 excludes
 * reaction-time constructs from the MVP because timing variance across the target Android fleet
 * swamps the signal, and Phase 0's triage additionally found inhibition has lower test-retest
 * reliability than working memory in this age band `[FACT: S14]` — so a cognitive-flexibility
 * SCORE is not something this product can honestly produce.
 *
 * What is recorded instead is untimed and behavioural: how many cards after a switch were still
 * sorted by the old rule. That is a count of observable actions, not a latency and not a trait.
 * Cognitive flexibility stays descriptive-only, exactly as the triage left it, and this task is
 * played for the play — it is the only game here where the ground moves under the child, which is
 * a genuinely different feeling from the other eight.
 */
/** Which property decides the bin. */
export const BY_SHAPE = 0;
export const BY_COLOR = 1;
/** Which bin a card belongs in under a rule. */
export function correctBin(card, rule, bins) {
    const property = rule === BY_SHAPE ? tokenShape : tokenColor;
    return property(card) === property(bins[0]) ? 0 : 1;
}
/**
 * Eight levels along three axes: more switches, shorter runs between them, and only then more cards.
 *
 * The run length does most of the work. A long run lets a child settle into the rule, which is
 * what makes the switch cost something; runs of two mean the rule never becomes a habit and the
 * child has to read the bins on every card. Nothing here approaches a size limit — the bins are
 * two large fixed targets, so this game could keep going long after the grid games stopped.
 *
 * **But the CHILD approaches a limit, and this ladder used to sail past it.** The rounds ran 8 to 22
 * cards and the sitting was the shared twelve trials, so a child resuming at the top of the ladder
 * was asked for two hundred and sixty cards in one go. `TEACH` budgets about five minutes on the
 * grounds that a preschooler leaves rather than finishes past it; that was three or four times over.
 * The task now carries its own `maxTrials` (see `task-defs.ts`) and the recipes below carry the other
 * half of the fix.
 *
 * **What was cut was slack, not difficulty.** What makes a round hard here is switch DENSITY — how
 * few cards the rule holds still for — and the top five levels were each paying four cards for
 * nothing: level eight promises eight switches at runs of two, which needs eighteen cards, and it
 * dealt twenty-two. Those four extra cards lowered the density of the level while lengthening it,
 * which is the wrong direction on both counts. Every level now runs at one or two cards above what
 * its switches and runs require, so each is denser than the level of the same name used to be and
 * all of them are shorter.
 *
 * Two cards of slack rather than none, deliberately: with none, the switch positions are the same in
 * every round of the level, and a child who has learnt "it changes every second card" is no longer
 * being caught out by anything — which is the event this game exists to produce.
 *
 * Level one loses its runs of four for the same reason it lost a card: eight cards with a single
 * change, always at the same place, is seven cards of doing one thing. Three is the floor its
 * sibling `key-map.ts` argues for at length, and it is enough to settle into.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { cards: 7, switches: 1, minRun: 3 };
        case 2:
            return { cards: 10, switches: 2, minRun: 3 };
        case 3:
            return { cards: 12, switches: 3, minRun: 3 };
        case 4:
            return { cards: 12, switches: 4, minRun: 2 };
        case 5:
            return { cards: 14, switches: 5, minRun: 2 };
        case 6:
            return { cards: 16, switches: 6, minRun: 2 };
        case 7:
            return { cards: 18, switches: 7, minRun: 2 };
        default:
            return { cards: 20, switches: 8, minRun: 2 };
    }
}
export const RULE_SWITCH_MIN_DIFFICULTY = 1;
export const RULE_SWITCH_MAX_DIFFICULTY = 8;
export function generateRuleSwitch(task, formId, trialIndex, difficulty) {
    if (difficulty < RULE_SWITCH_MIN_DIFFICULTY || difficulty > RULE_SWITCH_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { cards: cardCount, switches, minRun } = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    // Two bins that cross: different shape AND different colour, so the rules never agree.
    const shapeA = Math.floor(rand() * SHAPES);
    const shapeB = (shapeA + 1 + Math.floor(rand() * (SHAPES - 1))) % SHAPES;
    const colorA = Math.floor(rand() * COLORS);
    const colorB = (colorA + 1 + Math.floor(rand() * (COLORS - 1))) % COLORS;
    const bins = [packToken(shapeA, colorA), packToken(shapeB, colorB)];
    /**
     * Every card takes one shape and one colour from OPPOSITE bins.
     *
     * That is the whole design. A card with shape A and colour B goes left under the shape rule and
     * right under the colour rule, so each card's correct bin flips with the rule — and a child still
     * using the old rule is wrong every time rather than half the time. Without it, perseveration
     * would be invisible in the record.
     */
    const deck = [packToken(shapeA, colorB), packToken(shapeB, colorA)];
    /**
     * Switch points, spaced so each run is long enough to become a habit worth breaking.
     *
     * The slack is shared out rather than spent greedily. The first version stepped forward by
     * `minRun` plus a draw of up to `minRun` more each time and stopped when it ran out of room,
     * which quietly delivered fewer switches than the level asked for — at level eight, 7.7 of the 8
     * it promises, and at level three only 2.2 of 3. Tracking what is left to give keeps the count
     * exact while still moving the switches around between rounds.
     */
    const first = minRun;
    const last = cardCount - minRun;
    const span = last - first;
    const count = span < 0 ? 0 : Math.min(switches, Math.floor(span / minRun) + 1);
    const points = [];
    let slack = span - (count - 1) * minRun;
    let cursor = first;
    for (let i = 0; i < count; i++) {
        const give = slack > 0 ? Math.floor(rand() * (slack + 1)) : 0;
        cursor += (i === 0 ? 0 : minRun) + give;
        slack -= give;
        points.push(cursor);
    }
    let rule = rand() < 0.5 ? BY_SHAPE : BY_COLOR;
    const rules = [];
    for (let i = 0; i < cardCount; i++) {
        if (points.includes(i))
            rule = rule === BY_SHAPE ? BY_COLOR : BY_SHAPE;
        rules.push(rule);
    }
    /**
     * **The answer is built first and the card is read off it, which is the reverse of the obvious
     * way round and the only way a switch is guaranteed to be worth anything.**
     *
     * The obvious way was here and it was measurably broken. Cards were drawn independently of the
     * rules, and the correct bin is a function of BOTH — with two cards it is exactly their exclusive
     * or, so the bin changes only when one of them changes. At a switch point the rule flips; if the
     * drawn card flipped at the same index, the two cancelled and the right answer was the bin the
     * child had just pressed. **Two thirds of every switch in the game changed nothing observable.**
     * The bins visibly changed, the child re-read them, and the answer was the same as before.
     *
     * That is not a small loss. It is the one event this game exists to produce: at level one, which
     * has a single switch and is where a child meets the idea, sixty-nine percent of rounds contained
     * no observable rule change at all. The ladder's promise of more switches was worth a third of
     * what it said, and `perseverations` could only see the third that survived.
     *
     * So the sequence of correct bins is constructed with the properties it needs — it MUST change at
     * a switch, it drifts elsewhere, and it never runs more than three the same way — and the card is
     * then whichever one lands in that bin under the rule in force. Every rule change now moves the
     * answer, and the ladder means what it says.
     *
     * The card necessarily repeats at a switch, since bin and rule both flipping is the card not
     * flipping. That is not a tell worth hiding: the bins already announce the switch, deliberately
     * and visibly, so a second signal saying the same thing gives nothing away.
     */
    const cardFor = (bin, active) => (active === BY_SHAPE ? bin === 0 : bin === 1) ? deck[0] : deck[1];
    /**
     * Two runs are capped, not one, and they pull against each other.
     *
     * The card is the bin and the rule combined, so it moves only when exactly one of them moves.
     * At a switch the rule moves and the bin is made to move with it, which means **the card cannot
     * change at a switch** — that is forced by there being only two legal cards, and it is the price
     * of every switch being observable. The card's only chance to change is a bin flip between
     * switches.
     *
     * Capping the bin alone was not enough and the first attempt at this proved it: at the dense
     * levels the switches are two apart, the card was frozen through all of them, and a round came
     * out as eighteen identical pictures in a row. Capping the card alone brings back the other
     * failure — the bin sitting on one side for fifteen cards, rewarding the child for pressing the
     * same button. So both are counted and either one hitting three forces the flip, with a coin
     * deciding when neither is pressing.
     */
    const cards = [];
    let bin = rand() < 0.5 ? 0 : 1;
    let binRun = 1;
    let cardRun = 1;
    for (let i = 0; i < cardCount; i++) {
        if (i > 0) {
            const isSwitch = points.includes(i);
            const flip = isSwitch || cardRun >= 3 || binRun >= 3 || rand() < 0.5;
            // The card turns over when exactly one of the rule and the bin does.
            const cardTurned = isSwitch !== flip;
            if (flip) {
                bin = bin === 0 ? 1 : 0;
                binRun = 1;
            }
            else {
                binRun++;
            }
            cardRun = cardTurned ? 1 : cardRun + 1;
        }
        cards.push(cardFor(bin, rules[i]));
    }
    return { cards, rules, bins, switchPoints: points };
}
/**
 * Cards sorted by the PREVIOUS rule after a switch.
 *
 * The one number worth taking from this task, and it is a count of actions rather than a latency —
 * which is what keeps it inside PC-1. Recorded and described; never turned into a score for
 * cognitive flexibility, which stays descriptive-only in the Phase 0 triage.
 */
export function perseverations(round, chosen) {
    let count = 0;
    for (let i = 0; i < chosen.length && i < round.cards.length; i++) {
        const rule = round.rules[i];
        const card = round.cards[i];
        const answer = chosen[i];
        if (answer === correctBin(card, rule, round.bins))
            continue;
        // Wrong — but wrong in the specific way that means "still following the old rule".
        const previous = round.rules[i - 1];
        const stale = previous !== undefined && previous !== rule ? previous : undefined;
        const under = stale ?? (rule === BY_SHAPE ? BY_COLOR : BY_SHAPE);
        if (answer === correctBin(card, under, round.bins))
            count++;
    }
    return count;
}
/** How many cards went to the right bin. */
export function scoreRuleSwitch(round, chosen) {
    let correct = 0;
    for (let i = 0; i < chosen.length && i < round.cards.length; i++) {
        if (chosen[i] === correctBin(round.cards[i], round.rules[i], round.bins)) {
            correct++;
        }
    }
    return correct;
}
/**
 * A round counts as passed at three quarters correct.
 *
 * Not all of them, on purpose. Every switch costs a card or two even for an adult — that IS the
 * task — so demanding a clean sheet would fail every child for doing exactly what the task is
 * about, and the adaptive difficulty would drive them downward for it.
 */
export function passedRuleSwitch(round, chosen) {
    return scoreRuleSwitch(round, chosen) >= Math.ceil(round.cards.length * 0.75);
}
//# sourceMappingURL=rule-switch.js.map